import axios from 'axios';
import { AssetPrice, HistoricalData } from './api';

/**
 * CryptowatchR API服务
 * 主要用于获取加密货币数据
 * 免费API，无需API密钥
 */
class CryptowatchRService {
  private client = axios.create({
    baseURL: 'https://api.cryptowat.ch',
    timeout: 10000,
  });

  // 加密货币符号到交易对的映射
  private symbolToPair: Record<string, string> = {
    btc: 'btcusd',
    bitcoin: 'btcusd',
    eth: 'ethusd',
    ethereum: 'ethusd',
    bnb: 'bnbusd',
    sol: 'solusd',
    solana: 'solusd',
    ada: 'adausd',
    cardano: 'adausd',
    xrp: 'xrpusd',
    doge: 'dogeusd',
    dogecoin: 'dogeusd',
    dot: 'dotusd',
    polkadot: 'dotusd',
    matic: 'maticusd',
    polygon: 'maticusd',
    avax: 'avaxusd',
    avalanche: 'avaxusd',
    'avalanche-2': 'avaxusd',
    link: 'linkusd',
    chainlink: 'linkusd',
    uni: 'uniusd',
    uniswap: 'uniswap',
    ltc: 'ltcusd',
    litecoin: 'ltcusd',
    atom: 'atomusd',
    cosmos: 'atomusd',
    binancecoin: 'bnbusd',
    ripple: 'xrpusd',
  };

  // 默认使用的交易所（按优先级）
  private exchanges = ['binance', 'coinbase-pro', 'kraken', 'bitfinex'];

  /**
   * 获取加密货币价格
   */
  async getCryptoPrice(ids: string[]): Promise<AssetPrice[]> {
    try {
      const results: AssetPrice[] = [];

      for (const id of ids) {
        const pair = this.symbolToPair[id.toLowerCase()] || `${id.toLowerCase()}usd`;
        let priceData: AssetPrice | null = null;

        // 首先尝试使用markets/summaries端点批量获取
        try {
          const response = await this.client.get('/markets/summaries');
          const summaries = response.data.result || {};

          // 尝试从不同交易所获取数据
          for (const exchange of this.exchanges) {
            const marketKey = `${exchange}:${pair}`;
            const summary = summaries[marketKey];

            if (summary && summary.price) {
              const currentPrice = summary.price.last || summary.price.close || 0;
              const priceChange = summary.price.change?.absolute || 0;
              const priceChangePercent = summary.price.change?.percent || 0;

              priceData = {
                id: id.toLowerCase(),
                symbol: id.toUpperCase(),
                name: this.getAssetName(id),
                current_price: currentPrice,
                price_change_24h: priceChange,
                price_change_percentage_24h: priceChangePercent * 100, // 转换为百分比
                last_updated: new Date().toISOString(),
                source_url: this.client.defaults.baseURL || 'https://api.cryptowat.ch',
              };
              break;
            }
          }
        } catch (error) {
          // 如果批量获取失败，继续尝试单个市场端点
          console.warn('批量获取失败，尝试单个市场端点:', error);
        }

        // 如果批量获取失败，尝试单个市场端点
        if (!priceData) {
          priceData = await this.getSingleMarketPrice(id, pair);
        }

        if (priceData) {
          results.push(priceData);
        } else {
          // 如果无法获取价格，抛出错误而不是静默失败
          console.error(`无法从任何交易所获取 ${id} 的价格`);
          throw new Error(`Asset not found: ${id.toUpperCase()}`);
        }
      }

      return results;
    } catch (error) {
      console.error('CryptowatchR获取加密货币价格失败:', error);
      throw error;
    }
  }

  /**
   * 从单个市场获取价格
   */
  private async getSingleMarketPrice(id: string, pair: string): Promise<AssetPrice | null> {
    console.log(`尝试从单个市场获取 ${id} (${pair}) 的价格...`);
    
    for (const exchange of this.exchanges) {
      try {
        console.log(`尝试交易所: ${exchange}`);
        const response = await this.client.get(`/markets/${exchange}/${pair}/price`);
        const price = response.data.result?.price;

        if (price) {
          console.log(`成功从 ${exchange} 获取价格: ${price}`);
          // 获取24小时变化数据
          const tickerResponse = await this.client.get(`/markets/${exchange}/${pair}/ticker`).catch(() => null);
          const ticker = tickerResponse?.data?.result;
          
          const priceChange = ticker?.change?.absolute || 0;
          const priceChangePercent = (ticker?.change?.percent || 0) * 100;

          return {
            id: id.toLowerCase(),
            symbol: id.toUpperCase(),
            name: this.getAssetName(id),
            current_price: price,
            price_change_24h: priceChange,
            price_change_percentage_24h: priceChangePercent,
            last_updated: new Date().toISOString(),
            source_url: this.client.defaults.baseURL || 'https://api.cryptowat.ch',
          };
        } else {
          console.log(`${exchange} 返回了空价格`);
        }
      } catch (error) {
        // 继续尝试下一个交易所
        console.warn(`${exchange} 失败:`, error instanceof Error ? error.message : error);
        continue;
      }
    }
    
    console.error(`所有交易所都无法获取 ${id} (${pair}) 的价格`);
    return null;
  }

  /**
   * 获取历史数据
   */
  async getHistoricalData(id: string, days: number = 7): Promise<HistoricalData> {
    try {
      const pair = this.symbolToPair[id.toLowerCase()] || `${id.toLowerCase()}usd`;
      const periods = days <= 1 ? 60 : days <= 7 ? 3600 : days <= 30 ? 86400 : 86400;
      const ohlcPeriods = days <= 1 ? 60 : days <= 7 ? 3600 : 86400;

      // 尝试从不同交易所获取历史数据
      for (const exchange of this.exchanges) {
        try {
          const response = await this.client.get(`/markets/${exchange}/${pair}/ohlc`, {
            params: {
              periods: ohlcPeriods,
              after: Math.floor(Date.now() / 1000) - days * 86400,
            },
          });

          const ohlc = response.data.result?.[ohlcPeriods] || [];
          
          const prices: [number, number][] = ohlc.map((candle: number[]) => [
            candle[0] * 1000, // 时间戳（毫秒）
            candle[4], // 收盘价
          ]);

          // 计算市场总值和交易量（使用价格作为近似值）
          const market_caps: [number, number][] = prices.map(([time, price]) => [time, price * 1000000]);
          const total_volumes: [number, number][] = ohlc.map((candle: number[]) => [
            candle[0] * 1000,
            candle[5] || 0, // 交易量
          ]);

          return {
            prices,
            market_caps,
            total_volumes,
          };
        } catch (error) {
          continue;
        }
      }

      // 如果所有交易所都失败，返回空数据
      return {
        prices: [],
        market_caps: [],
        total_volumes: [],
      };
    } catch (error) {
      console.error('CryptowatchR获取历史数据失败:', error);
      throw error;
    }
  }

  /**
   * 获取资产名称
   */
  private getAssetName(id: string): string {
    const nameMap: Record<string, string> = {
      btc: 'Bitcoin',
      bitcoin: 'Bitcoin',
      eth: 'Ethereum',
      ethereum: 'Ethereum',
      bnb: 'BNB',
      binancecoin: 'BNB',
      sol: 'Solana',
      solana: 'Solana',
      ada: 'Cardano',
      cardano: 'Cardano',
      xrp: 'XRP',
      ripple: 'XRP',
      doge: 'Dogecoin',
      dogecoin: 'Dogecoin',
      dot: 'Polkadot',
      polkadot: 'Polkadot',
      matic: 'Polygon',
      polygon: 'Polygon',
      avax: 'Avalanche',
      avalanche: 'Avalanche',
      'avalanche-2': 'Avalanche',
      link: 'Chainlink',
      chainlink: 'Chainlink',
      uni: 'Uniswap',
      uniswap: 'Uniswap',
      ltc: 'Litecoin',
      litecoin: 'Litecoin',
      atom: 'Cosmos',
      cosmos: 'Cosmos',
    };
    return nameMap[id.toLowerCase()] || id;
  }
}

export const cryptowatchrService = new CryptowatchRService();

